import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    ExperimentOutlined,
    LinkOutlined,
} from '@ant-design/icons';
import type {
    ClinicalTrialCriterion,
    ClinicalTrialJob,
    CriteriaGroup,
    LogicalOperator,
} from '@ui/features/ClinicalTrial/types';
import { Popover, Tag } from 'antd';
import type { ReactNode } from 'react';

interface ClinicalTrialInfoPopoverProps {
    jobData: ClinicalTrialJob;
    children: ReactNode;
}

function CriterionRow({ criterion, type }: { criterion: ClinicalTrialCriterion; type: 'inclusion' | 'exclusion' }) {
    const isFailed = criterion.execution_status === 'failed';
    const isActive = criterion.is_selected && !isFailed;

    const icon = isFailed ? (
        <ExclamationCircleOutlined className="mt-0.5 text-amber-500" />
    ) : type === 'inclusion' ? (
        <CheckCircleOutlined className="mt-0.5 text-emerald-500" />
    ) : (
        <CloseCircleOutlined className="mt-0.5 text-red-400" />
    );

    return (
        <div className={`flex items-start gap-2 rounded px-2 py-1 text-xs ${isActive ? 'bg-slate-50' : 'opacity-50'}`}>
            {icon}
            <div className="flex flex-1 items-start gap-1.5">
                <Tag
                    color={isFailed ? 'red' : type === 'inclusion' ? 'blue' : 'orange'}
                    className="mr-0 text-[10px]"
                >
                    {criterion.criterion_id}
                </Tag>
                <span className="flex-1 text-slate-600">{criterion.query_text}</span>
            </div>
            {isFailed ? (
                <span className="ml-1 whitespace-nowrap text-[10px] text-red-400">failed</span>
            ) : (
                criterion.patient_count != null && (
                    <span className="ml-1 whitespace-nowrap font-mono text-[10px] text-slate-400">
                        {criterion.patient_count.toLocaleString()}
                    </span>
                )
            )}
        </div>
    );
}

function OperatorBadge({ operator }: { operator: LogicalOperator }) {
    const isAnd = operator === 'AND';
    return (
        <span
            className={`inline-flex items-center rounded-full px-1.5 py-px text-[9px] font-bold ${
                isAnd ? 'bg-[#DCE0FB] text-[#203B87]' : 'bg-[#FFE8D0] text-[#FF8400]'
            }`}
        >
            {operator}
        </span>
    );
}

function GroupedSection({
    type,
    groups,
    interGroupOperator,
    criteriaMap,
}: {
    type: 'inclusion' | 'exclusion';
    groups: CriteriaGroup[];
    interGroupOperator: LogicalOperator;
    criteriaMap: Map<string, ClinicalTrialCriterion>;
}) {
    const nonEmptyGroups = groups.filter(g => g.criteriaIds.length > 0);
    if (nonEmptyGroups.length === 0) return null;

    const isInclusion = type === 'inclusion';
    const badge = isInclusion ? (
        <span className="inline-flex h-4 items-center rounded bg-emerald-100 px-1.5 text-[10px] font-semibold text-emerald-700">
            INCLUDE
        </span>
    ) : (
        <span className="inline-flex h-4 items-center rounded bg-red-100 px-1.5 text-[10px] font-semibold text-red-700">
            EXCLUDE
        </span>
    );

    const totalCriteria = nonEmptyGroups.reduce((sum, g) => sum + g.criteriaIds.length, 0);

    return (
        <div>
            <div className="mb-1.5 flex items-center gap-2">
                {badge}
                <span className="text-xs text-slate-400">
                    {totalCriteria} criteria in {nonEmptyGroups.length} group{nonEmptyGroups.length > 1 ? 's' : ''}
                </span>
            </div>
            <div className="space-y-1.5">
                {nonEmptyGroups.map((group, gIdx) => (
                    <div key={group.id}>
                        {gIdx > 0 && (
                            <div className="flex justify-center py-0.5">
                                <OperatorBadge operator={interGroupOperator} />
                            </div>
                        )}
                        <div className="rounded border border-slate-100 bg-slate-50/50 px-1 py-1">
                            <div className="mb-0.5 flex items-center gap-1.5 px-1">
                                <span className="text-[10px] font-medium text-slate-400">Group {gIdx + 1}</span>
                                <OperatorBadge operator={group.operator} />
                            </div>
                            <div>
                                {group.criteriaIds.map(id => {
                                    const c = criteriaMap.get(id);
                                    if (!c) return null;
                                    return (
                                        <CriterionRow
                                            key={id}
                                            criterion={c}
                                            type={type}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function FlatSection({ type, criteria }: { type: 'inclusion' | 'exclusion'; criteria: ClinicalTrialCriterion[] }) {
    if (criteria.length === 0) return null;

    const isInclusion = type === 'inclusion';
    const badge = isInclusion ? (
        <span className="inline-flex h-4 items-center rounded bg-emerald-100 px-1.5 text-[10px] font-semibold text-emerald-700">
            INCLUDE
        </span>
    ) : (
        <span className="inline-flex h-4 items-center rounded bg-red-100 px-1.5 text-[10px] font-semibold text-red-700">
            EXCLUDE
        </span>
    );

    return (
        <div>
            <div className="mb-1.5 flex items-center gap-2">
                {badge}
                <span className="text-xs text-slate-400">
                    {criteria.filter(c => c.is_selected).length}/{criteria.length} selected
                </span>
            </div>
            <div className="space-y-1">
                {criteria.map(c => (
                    <CriterionRow
                        key={c.id}
                        criterion={c}
                        type={type}
                    />
                ))}
            </div>
        </div>
    );
}

function PopoverContent({ jobData }: { jobData: ClinicalTrialJob }) {
    const inclusionCriteria = jobData.criteria?.filter(c => c.criterion_type === 'inclusion') || [];
    const exclusionCriteria = jobData.criteria?.filter(c => c.criterion_type === 'exclusion') || [];
    const latestExecution = jobData.executions?.[0];
    const qc = jobData.query_config;

    const criteriaMap = new Map((jobData.criteria || []).map(c => [c.id, c]));

    return (
        <div className="min-w-[320px] max-w-md">
            {/* Trial Metadata */}
            <div className="space-y-2 border-b border-slate-100 pb-3">
                {jobData.nct_id && (
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-slate-400">NCT ID</span>
                        <a
                            href={`https://clinicaltrials.gov/study/${jobData.nct_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-sm font-medium text-blue-600 hover:underline"
                        >
                            {jobData.nct_id} <LinkOutlined className="text-xs" />
                        </a>
                    </div>
                )}
                <div className="flex gap-4">
                    {jobData.trial_phase && (
                        <div className="flex items-center gap-1.5">
                            <span className="text-xs text-slate-400">Phase</span>
                            <span className="text-sm text-slate-700">{jobData.trial_phase}</span>
                        </div>
                    )}
                    {jobData.trial_status && (
                        <div className="flex items-center gap-1.5">
                            <span className="text-xs text-slate-400">Status</span>
                            <span className="text-sm text-slate-700">{jobData.trial_status.replace(/_/g, ' ')}</span>
                        </div>
                    )}
                </div>
                {jobData.trial_conditions && jobData.trial_conditions.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                        {jobData.trial_conditions.map(condition => (
                            <Tag
                                key={condition}
                                className="text-xs"
                            >
                                {condition}
                            </Tag>
                        ))}
                    </div>
                )}
                {latestExecution?.final_patient_count != null && (
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs text-slate-400">Matched Patients</span>
                        <span className="font-mono text-sm font-semibold text-slate-900">
                            {latestExecution.final_patient_count.toLocaleString()}
                        </span>
                    </div>
                )}
            </div>

            {/* Criteria — grouped view when query_config exists, flat list otherwise */}
            {(inclusionCriteria.length > 0 || exclusionCriteria.length > 0) && (
                <div className="mt-3 max-h-[300px] space-y-3 overflow-y-auto">
                    {qc ? (
                        <>
                            <GroupedSection
                                type="inclusion"
                                groups={qc.inclusionGroups}
                                interGroupOperator={qc.inclusionGroupOperator}
                                criteriaMap={criteriaMap}
                            />
                            <GroupedSection
                                type="exclusion"
                                groups={qc.exclusionGroups}
                                interGroupOperator={qc.exclusionGroupOperator}
                                criteriaMap={criteriaMap}
                            />
                        </>
                    ) : (
                        <>
                            <FlatSection
                                type="inclusion"
                                criteria={inclusionCriteria}
                            />
                            <FlatSection
                                type="exclusion"
                                criteria={exclusionCriteria}
                            />
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default function ClinicalTrialInfoPopover({ jobData, children }: ClinicalTrialInfoPopoverProps) {
    return (
        <Popover
            content={<PopoverContent jobData={jobData} />}
            title={
                <div className="flex items-center gap-2">
                    <ExperimentOutlined className="text-blue-500" />
                    <span className="text-sm font-medium text-slate-800">
                        {jobData.trial_title || jobData.name || 'Clinical Trial'}
                    </span>
                </div>
            }
            trigger="hover"
            placement="bottomLeft"
            overlayStyle={{ maxWidth: '460px' }}
            mouseEnterDelay={0.3}
        >
            {children}
        </Popover>
    );
}
